import type { FieldDefinition } from "@estelle/domain";

/**
 * The fields the Customers section offers to the filter builder, the column
 * chooser and the sort control (#447).
 *
 * Declared apart from `CONTACT_FIELDS`: this section lists Shopify's customers,
 * not the contacts the team keeps, and the two describe different rows. One
 * vocabulary for both would open Customers with a GSTIN column of em dashes.
 *
 * Every name here is projected by both adapters — the API's SQL field map
 * (`apps/api/src/routes/customers.ts`) and the fixture adapter's projection —
 * because a field that is offered and matches nothing reads as missing data.
 *
 * `ordersCount`, `totalSpent` and `lastOrderAt` are computed from the orders
 * that are not cancelled, not Shopify's lifetime counters.
 *
 * `phone` and `email` are contact details: the server sends them only to
 * someone holding `customers.view_contact` (`CUSTOMER_FIELD_PERMISSIONS`
 * below), so for anyone else they are not offered here either, and cannot be
 * used to ask whether a number exists.
 *
 * `tags` and `note` are the team's own, held beside the mirror and never sent
 * to Shopify. The note is left out of the starting columns: free text makes
 * every row tall, and the chooser offers it for whoever wants it.
 */
export const CUSTOMER_FIELDS: FieldDefinition[] = [
  { name: "displayName", label: "Name", type: "text", sortable: true },
  { name: "phone", label: "Phone", type: "text", sortable: true },
  { name: "email", label: "Email", type: "text", sortable: true },
  { name: "city", label: "City", type: "text", sortable: true },
  { name: "state", label: "State", type: "text", sortable: true },
  { name: "ordersCount", label: "Orders", type: "number", sortable: true },
  { name: "totalSpent", label: "Total spent", type: "money", sortable: true },
  { name: "lastOrderAt", label: "Last order", type: "date", sortable: true },
  { name: "tags", label: "Tags", type: "tag" },
  {
    name: "note",
    label: "Note",
    type: "text",
    sortable: true,
    defaultHidden: true,
  },
];

/**
 * The permission each protected field needs, by field name.
 *
 * `SectionPage` drops a field the signed-in user lacks the permission for
 * before the toolbar or the table sees it. A courtesy, not the enforcement:
 * the API does not send the values, and ignores a filter, sort or search that
 * names them, whatever the page offers.
 */
export const CUSTOMER_FIELD_PERMISSIONS: Record<string, string> = {
  phone: "customers.view_contact",
  email: "customers.view_contact",
};
